import { useState, useCallback, useMemo } from 'react';
import type { FormSuggestion, FormSuggestionsResponse } from '../components/AISuggestions';

interface SuggestionState {
  suggestion: FormSuggestion;
  status: 'pending' | 'accepted' | 'rejected';
  appliedAt?: Date;
}

interface SuggestionHistory {
  action: 'accept' | 'reject' | 'accept-all' | 'reject-all';
  field?: string;
  previousState: Record<string, SuggestionState>;
  timestamp: Date;
}

export interface SuggestionManagerState {
  suggestions: Record<string, SuggestionState>;
  history: SuggestionHistory[];
  historyIndex: number;
  analysisState: 'idle' | 'analyzing' | 'complete' | 'error';
  analysisError?: string;
  overallConfidence?: 'low' | 'medium' | 'high';
  analysisNotes?: string;
  
  // Computed properties
  canUndo: boolean;
  canRedo: boolean;
  acceptedCount: number;
  rejectedCount: number;
  totalCount: number;
  hasAnySuggestions: boolean;
}

export interface SuggestionManagerActions {
  // Core suggestion management
  setSuggestions: (suggestions: FormSuggestionsResponse) => void;
  acceptSuggestion: (field: string) => void;
  rejectSuggestion: (field: string) => void;
  acceptAllSuggestions: () => void;
  rejectAllSuggestions: () => void;
  clearAllSuggestions: () => void;
  
  // Analysis state management
  setAnalysisState: (state: 'idle' | 'analyzing' | 'complete' | 'error') => void;
  setAnalysisError: (error?: string) => void;
  
  // History management
  undo: () => void;
  redo: () => void;
  
  // Getters
  getFieldSuggestion: (field: string) => SuggestionState | undefined;
  getAcceptedValues: () => Record<string, string>;
  getAcceptedValue: (field: string) => string | undefined;
}

const initialState: SuggestionManagerState = {
  suggestions: {},
  history: [],
  historyIndex: -1,
  analysisState: 'idle',
  
  // Computed properties with default values
  canUndo: false,
  canRedo: false,
  acceptedCount: 0,
  rejectedCount: 0,
  totalCount: 0,
  hasAnySuggestions: false,
};

export function useSuggestionManager(): SuggestionManagerState & SuggestionManagerActions {
  const [state, setState] = useState<SuggestionManagerState>(initialState);

  // Helper function to create a history entry
  const createHistoryEntry = useCallback((
    action: SuggestionHistory['action'],
    field?: string,
    previousState?: Record<string, SuggestionState>
  ): SuggestionHistory => ({
    action,
    field,
    previousState: previousState || state.suggestions,
    timestamp: new Date(),
  }), [state.suggestions]);

  // Helper function to add to history and update state
  const updateWithHistory = useCallback((
    newSuggestions: Record<string, SuggestionState>,
    action: SuggestionHistory['action'],
    field?: string
  ) => {
    setState(prevState => {
      const historyEntry = createHistoryEntry(action, field, prevState.suggestions);
      
      // Truncate history if we're not at the end (user did something after undoing)
      const newHistory = prevState.history.slice(0, prevState.historyIndex + 1);
      newHistory.push(historyEntry);
      
      // Limit history to last 50 entries to prevent memory issues
      const limitedHistory = newHistory.slice(-50);
      
      return {
        ...prevState,
        suggestions: newSuggestions,
        history: limitedHistory,
        historyIndex: limitedHistory.length - 1,
      };
    });
  }, [createHistoryEntry]);

  // Set suggestions from AI analysis response
  const setSuggestions = useCallback((response: FormSuggestionsResponse) => {
    const newSuggestions: Record<string, SuggestionState> = {};
    
    response.suggestions.forEach(suggestion => {
      newSuggestions[suggestion.field] = {
        suggestion,
        status: 'pending',
      };
    });

    setState(prevState => ({
      ...prevState,
      suggestions: newSuggestions,
      overallConfidence: response.overallConfidence,
      analysisNotes: response.analysisNotes,
      analysisState: 'complete',
      analysisError: undefined,
      // Reset history when new suggestions arrive
      history: [],
      historyIndex: -1,
    }));
  }, []);

  // Accept a single suggestion
  const acceptSuggestion = useCallback((field: string) => {
    const currentSuggestion = state.suggestions[field];
    if (!currentSuggestion || currentSuggestion.status === 'accepted') return;

    const newSuggestions = {
      ...state.suggestions,
      [field]: {
        ...currentSuggestion,
        status: 'accepted' as const,
        appliedAt: new Date(),
      },
    };

    updateWithHistory(newSuggestions, 'accept', field);
  }, [state.suggestions, updateWithHistory]);

  // Reject a single suggestion
  const rejectSuggestion = useCallback((field: string) => {
    const currentSuggestion = state.suggestions[field];
    if (!currentSuggestion || currentSuggestion.status === 'rejected') return;

    const newSuggestions = {
      ...state.suggestions,
      [field]: {
        ...currentSuggestion,
        status: 'rejected' as const,
      },
    };

    updateWithHistory(newSuggestions, 'reject', field);
  }, [state.suggestions, updateWithHistory]);

  // Accept all pending suggestions
  const acceptAllSuggestions = useCallback(() => {
    const newSuggestions = { ...state.suggestions };
    let hasChanges = false;

    Object.keys(newSuggestions).forEach(field => {
      if (newSuggestions[field].status === 'pending') {
        newSuggestions[field] = {
          ...newSuggestions[field],
          status: 'accepted',
          appliedAt: new Date(),
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      updateWithHistory(newSuggestions, 'accept-all');
    }
  }, [state.suggestions, updateWithHistory]);

  // Reject all pending suggestions
  const rejectAllSuggestions = useCallback(() => {
    const newSuggestions = { ...state.suggestions };
    let hasChanges = false;

    Object.keys(newSuggestions).forEach(field => {
      if (newSuggestions[field].status !== 'rejected') {
        newSuggestions[field] = {
          ...newSuggestions[field],
          status: 'rejected',
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      updateWithHistory(newSuggestions, 'reject-all');
    }
  }, [state.suggestions, updateWithHistory]);

  // Undo last action
  const undo = useCallback(() => {
    if (state.historyIndex < 0) return;

    const historyEntry = state.history[state.historyIndex];
    setState(prevState => ({
      ...prevState,
      suggestions: historyEntry.previousState,
      historyIndex: prevState.historyIndex - 1,
    }));
  }, [state.historyIndex, state.history]);

  // Redo last undone action
  const redo = useCallback(() => {
    if (state.historyIndex >= state.history.length - 1) return;

    const nextIndex = state.historyIndex + 1;
    const nextHistoryEntry = state.history[nextIndex];
    
    // We need to reconstruct the state that was active after this action
    // This is a bit complex, but we can derive it from the action type
    setState(prevState => {
      const newSuggestions = { ...nextHistoryEntry.previousState };
      
      switch (nextHistoryEntry.action) {
        case 'accept':
          if (nextHistoryEntry.field) {
            newSuggestions[nextHistoryEntry.field] = {
              ...newSuggestions[nextHistoryEntry.field],
              status: 'accepted',
              appliedAt: new Date(),
            };
          }
          break;
        case 'reject':
          if (nextHistoryEntry.field) {
            newSuggestions[nextHistoryEntry.field] = {
              ...newSuggestions[nextHistoryEntry.field],
              status: 'rejected',
            };
          }
          break;
        case 'accept-all':
          Object.keys(newSuggestions).forEach(field => {
            if (newSuggestions[field].status === 'pending') {
              newSuggestions[field] = {
                ...newSuggestions[field],
                status: 'accepted',
                appliedAt: new Date(),
              };
            }
          });
          break;
        case 'reject-all':
          Object.keys(newSuggestions).forEach(field => {
            newSuggestions[field] = {
              ...newSuggestions[field],
              status: 'rejected',
            };
          });
          break;
      }

      return {
        ...prevState,
        suggestions: newSuggestions,
        historyIndex: nextIndex,
      };
    });
  }, [state.historyIndex, state.history]);

  // Clear all suggestions and reset state
  const clearAllSuggestions = useCallback(() => {
    setState(initialState);
  }, []);

  // Set analysis state
  const setAnalysisState = useCallback((
    analysisState: 'idle' | 'analyzing' | 'complete' | 'error'
  ) => {
    setState(prevState => ({
      ...prevState,
      analysisState,
    }));
  }, []);

  // Set analysis error
  const setAnalysisError = useCallback((
    error?: string
  ) => {
    setState(prevState => ({
      ...prevState,
      analysisError: error,
    }));
  }, []);

  // Get accepted values as a simple object
  const getAcceptedValues = useCallback((): Record<string, string> => {
    const accepted: Record<string, string> = {};
    Object.entries(state.suggestions).forEach(([field, suggestionState]) => {
      if (suggestionState.status === 'accepted') {
        accepted[field] = suggestionState.suggestion.value;
      }
    });
    return accepted;
  }, [state.suggestions]);

  // Get a specific accepted value
  const getAcceptedValue = useCallback((field: string): string | undefined => {
    const suggestionState = state.suggestions[field];
    return suggestionState?.status === 'accepted' ? suggestionState.suggestion.value : undefined;
  }, [state.suggestions]);

  // Get suggestion for a specific field
  const getFieldSuggestion = useCallback((field: string): SuggestionState | undefined => {
    return state.suggestions[field];
  }, [state.suggestions]);

  // Computed values
  const canUndo = state.historyIndex >= 0;
  const canRedo = state.historyIndex < state.history.length - 1;
  
  const acceptedCount = useMemo(() => {
    return Object.values(state.suggestions).filter(s => s.status === 'accepted').length;
  }, [state.suggestions]);
  
  const rejectedCount = useMemo(() => {
    return Object.values(state.suggestions).filter(s => s.status === 'rejected').length;
  }, [state.suggestions]);
  
  const totalCount = Object.keys(state.suggestions).length;
  const hasAnySuggestions = totalCount > 0;

  return {
    ...state,
    setSuggestions,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    rejectAllSuggestions,
    clearAllSuggestions,
    setAnalysisState,
    setAnalysisError,
    undo,
    redo,
    getFieldSuggestion,
    getAcceptedValues,
    getAcceptedValue,
    canUndo,
    canRedo,
    acceptedCount,
    rejectedCount,
    totalCount,
    hasAnySuggestions,
  };
} 